import React, { ReactElement, isValidElement, useCallback } from 'react';
import { isNumber } from 'mathjs';
import Spacer from './Spacer';
import WithDefaultEvents, { IWithDefaultEventsProps } from '../reactUtils/WithDefaultEvents';
import { isTangible } from '../../classes/utilities/Misc';

interface ITableProps {
	children?: React.ReactNode;
	"data-name"?: string;
	style?: React.CSSProperties;
	rowSpacing?: number;
	colSpacing?: number;
	className?: string;
	skipRows?: number;
}

export function Table(props: ITableProps) 
{
	const countMaxColumns = useCallback(() =>
	{
		let maxColumns = 0;

		React.Children.forEach(props.children, (row) => 
		{
			if (!row) return;
			if (!isValidElement(row) || !(row as ReactElement).props)
			{
				maxColumns = Math.max(maxColumns, 1);
				return;
			}

			maxColumns = Math.max(maxColumns, React.Children.count((row as ReactElement).props.children));
		});

		return maxColumns;
	}, [props.children]);

	const calculateColumnDimensions = useCallback((columnCount: number) =>
	{
		const columnWidths = new Array<number>(columnCount).fill(-1);

		// find the widest cell in each column
		React.Children.forEach(props.children, (row) => 
		{
			if (!row || !isValidElement(row)) return;

			const rowElement = row as ReactElement;
			React.Children.forEach(rowElement.props.children, (cell, i) => 
			{
				if (!cell || !isValidElement(cell)) return;

				const cellElement = cell as ReactElement;
				if (cellElement.props.width && isNumber(cellElement.props.width)) 
				{
					columnWidths[i] = Math.max(columnWidths[i], cellElement.props.width);
				}
			});
		});

		// total the fixed width
		const fixedWidthColumns = columnWidths.filter(v => v !== -1);
		let totalFixedWidth = fixedWidthColumns.reduce((a, b) => a + b, 0);
		if (props.colSpacing) 
		{
			totalFixedWidth += (columnCount - 1) * props.colSpacing;
		}

		const output = new Array<string>(columnCount);

		// calculate the width of each column, whether fixed or not
		for (let i = 0; i < columnCount; i++) 
		{
			if (columnWidths[i] !== -1) 
			{
				output[i] = `${columnWidths[i]}px`;
			}

			else 
			{
				output[i] = `calc((100% - ${totalFixedWidth}px) / ${columnCount - fixedWidthColumns.length})`;
			}
		}

		return output;
	}, [props.children, props.colSpacing]);

	// TODO: when a row has fewer cells than maxColumns, we need to insert dummy cells after colSpan in the appropriate amount
	function generateTable() 
	{
		if (!props.children) return;

		const output = new Array<React.ReactNode>();
		let k = 0;

		const children = React.Children.toArray(props.children).filter(c => isTangible(c));
		const columnCount = countMaxColumns();
		const columnDimensions = calculateColumnDimensions(columnCount);

		children.forEach((row, i) => 
		{
			if (!row) return;
			if (props.skipRows && i < props.skipRows) return;

			// if the row is not actually a row, and not even a valid element, we wrap it in a row and a cell
			if (!isValidElement(row) || (row as ReactElement).type !== TableRow) 
			{
				console.warn(`Table child [${i}] should be of type TableRow, but instead it is of type <${typeof row}>. This can lead to unexpected behavior.`);

				output.push(
					<TableRow key={k++}>
						<TableCell
							style={{
								width: columnDimensions.at(0) ?? "100%",
							}}
						>
							{row}
						</TableCell>
					</TableRow>
				);

				return;
			}

			// it's a row, so we need to iterate on its children and generate the cells
			const rowElement = row as ReactElement;
			const rowChildren = React.Children.toArray(rowElement.props.children);
			const rowOutput = new Array<React.ReactNode>(columnCount);

			for (let c = 0; c < columnCount; c++) 
			{
				const rowCell = rowChildren.at(c);

				function addSpacingBetweenCells() 
				{
					if (props.colSpacing && c < columnCount - 1) 
					{
						rowOutput.push(<Spacer size={{ x: `${props.colSpacing}px` }} key={k++} />);
					}
				}

				if (!rowCell) 
				{
					rowOutput.push(
						<TableCell key={k++}
							style={{
								width: columnDimensions.at(c),
							}} />
					);
					addSpacingBetweenCells();
					continue;
				}

				// if the cell is not actually a cell, and not even a valid element, we wrap it in a cell
				if (!isValidElement(rowCell) || (rowCell as ReactElement).type !== TableCell) 
				{
					console.warn(`Table child [${i}] child [${c}] should be of type TableCell, but instead it is of type <${typeof rowCell}>. This can lead to unexpected behavior.`);

					rowOutput.push(
						<TableCell key={k++}
							style={{
								width: columnDimensions.at(c),
							}}
						>
							{rowCell}
						</TableCell>
					);
					addSpacingBetweenCells();
					continue;
				}

				// it's a cell, so we add it to the row output
				const cellElement = rowCell as ReactElement;

				let cellWidth = columnDimensions.at(c);

				// we need to consider colSpan here, skip the next X cells, and calculate the width of the cel properly
				if(cellElement.props.colSpan && isNumber(cellElement.props.colSpan))
				{
					const colSpan = cellElement.props.colSpan;
					for(let j = 1; j < colSpan; j++) // skip the first column since we initialize to it above
					{
						cellWidth += ` + calc(${columnDimensions.at(c + j)} + ${props.colSpacing}px)`;
					}
					cellWidth = `calc(${cellWidth})`; // remove the last colSpacing

					c += colSpan - 1; // skip the next X cells
				}

				rowOutput.push(
					<TableCell key={k++}
						{...cellElement.props}
						style={{
							...cellElement.props.style,
							width: cellWidth,
						}}
					>
						{cellElement.props.children}
					</TableCell>
				);

				addSpacingBetweenCells();
			}

			// add the row to the output
			output.push(
				<TableRow key={k++}
					{...rowElement.props}
				>
					{rowOutput}
				</TableRow>
			);

			// add spacing between rows
			if (props.rowSpacing && i < children.length - 1)
			{
				output.push(<Spacer size={{ y: `${props.rowSpacing}px` }} key={k++} />);
			}
		});

		return output;
	}

	return (
		<div data-name={props['data-name'] ?? "" + "-table-root"}
			className={["tableRoot", props.className].join(" ")}
			style={props.style}
		>
			<div data-name={"table-content"}
				className={["tableContent", props.className].join(" ")}
			>
				{generateTable()}
			</div>
		</div>
	);
}

interface ITableRowProps extends IWithDefaultEventsProps {
	style?: React.CSSProperties;
	"data-name"?: string;
	"data-tooltip"?: string;
	className?: string;
	height?: number;
}

function TableRowComponent(props: ITableRowProps)
{
	return (
		<div data-name={props['data-name'] ?? "" + "-table-row"}
			data-tooltip={props['data-tooltip']}
			className={["tableRow", props.className].join(" ")}
			style={props.style}
			onClick={props.onClick}
			onMouseDown={props.onMouseDown}
			onContextMenu={props.onContextMenu}
			onDoubleClick={props.onDoubleClick}
		>
			{props.children}
		</div>
	);
}
export const TableRow = WithDefaultEvents(TableRowComponent);

interface ITableCellProps extends IWithDefaultEventsProps {
	style?: React.CSSProperties;
	"data-name"?: string;
	className?: string;
	colSpan?: number;
	width?: number;
}

function TableCellComponent(props: ITableCellProps)
{
	return (
		<div data-name={props['data-name'] ?? "" + "-table-cell"}
			className={["tableCell", props.className].join(" ")}
			style={props.style}
			onClick={props.onClick}
			onMouseDown={props.onMouseDown}
			onContextMenu={props.onContextMenu}
			onDoubleClick={props.onDoubleClick}
		>
			{props.children}
		</div>	
	);
}
export const TableCell = WithDefaultEvents(TableCellComponent);